import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Switch } from "../../components/ui/switch";
import { Users, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import apiService from "../../services/apiService";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";



type TeamMember = {
  id: string;
  name: string;
  email: string;
  phone: string;
  designation: string;
  isActive: boolean;
  first_name: string;
  last_name: string;
};

type TeamMembersApiItem = {
  id?: string | number;
  team_member_id?: string | number;
  user_id?: string | number;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
  designation?: string;
  is_active?: boolean;
};

type TeamMembersApiResponse = {
  data?: TeamMembersApiItem[];
  result?: {
    data?: TeamMembersApiItem[];
  };
} | TeamMembersApiItem[];

type NewMemberForm = {
  firstName: string;
  lastName: string;
  email: string;
  designation: string;
  phone: string;
};

type TeamMembersContentProps = {
  isActive: boolean;
};

const TeamMembersContent = ({ isActive }: TeamMembersContentProps) => {
  const { customerId } = useParams();
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [deleteMemberId, setDeleteMemberId] = useState<string | null>(null);
  const [toggleMemberId, setToggleMemberId] = useState<string | null>(null);
  const [isInvitingMember, setIsInvitingMember] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [isTogglingMemberStatus, setIsTogglingMemberStatus] = useState(false);
  const [newMemberForm, setNewMemberForm] = useState<NewMemberForm>({
    firstName: "",
    lastName: "",
    email: "",
    designation: "",
    phone: "",
  });

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  const mapTeamMemberFromApi = useCallback((member: TeamMembersApiItem): TeamMember => {
    const firstName = (member.first_name || member.firstName || "").trim();
    const lastName = (member.last_name || member.lastName || "").trim();
    const fullName = `${firstName} ${lastName}`.trim() || member.name?.trim() || "";

    return {
      id: String(member.id || member.team_member_id || member.user_id || crypto.randomUUID()),
      name: fullName || "N/A",
      first_name: firstName,
      last_name: lastName,
      email: member.email || "-",
      phone: member.phone || "-",
      designation: member.designation || "-",
      isActive: member.is_active === true,
    };
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    if (!customerId || !isActive) return;

    try {
      const response = await apiService.get<TeamMembersApiResponse>(
        `/v1/getCustomerTeamMembers/${customerId}`
      );

      const membersArray = Array.isArray(response)
        ? response
        : response?.data || response?.result?.data || [];

      setTeamMembers(membersArray.map(mapTeamMemberFromApi));
    } catch (error) {
      console.error("Error fetching customer team members:", error);
      toast.error("Failed to fetch team members.");
    }
  }, [customerId, isActive, mapTeamMemberFromApi]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const isAddMemberFormValid =
    newMemberForm.firstName.trim() !== "" &&
    newMemberForm.lastName.trim() !== "" &&
    newMemberForm.email.trim() !== "" &&
    newMemberForm.designation.trim() !== "" &&
    newMemberForm.phone.trim().length >= 10;

  const resetNewMemberForm = () => {
    setNewMemberForm({
      firstName: "",
      lastName: "",
      email: "",
      designation: "",
      phone: "",
    });
  };

  const handleAddMemberDialogChange = (open: boolean) => {
    if (isInvitingMember) return;
    setAddMemberOpen(open);
    if (!open) {
      resetNewMemberForm();
    }
  };

  const handleAddMember = async () => {
    if (!isAddMemberFormValid) return;
    if (!customerId) {
      toast.error("Customer ID is missing.");
      return;
    }

    try {
      setIsInvitingMember(true);
      await apiService.post("/v1/create_customer_team", {
        customerId,
        first_name: newMemberForm.firstName.trim(),
        last_name: newMemberForm.lastName.trim(),
        email: newMemberForm.email.trim(),
        phone: newMemberForm.phone.trim(),
        designation: newMemberForm.designation.trim(),
      });
      await fetchTeamMembers();

      toast.success("An invitation email has been sent.");
      setAddMemberOpen(false);
      resetNewMemberForm();
    } catch (error) {
      console.error("Error creating customer team member:", error);
      toast.error((error as any).message);
    } finally {
      setIsInvitingMember(false);
    }
  };
  const handleToggleMemberStatus = async (memberId: string) => {
    try {
      setIsTogglingMemberStatus(true);
      await apiService.put("/v1/setCustomerTeamMembersInActive", {
        memberId,
      });
      toast.success("Team member status has been changed.");
      setToggleMemberId(null);
      await fetchTeamMembers();
    } catch (error) {
      console.error("Error updating team member status:", error);
      toast.error((error as { message?: string })?.message || "Failed to update member status.");
    } finally {
      setIsTogglingMemberStatus(false);
    }
  };
  const handleDeleteMember = async (memberId: string) => {
    try {
      setIsRemovingMember(true);
      await apiService.put("/v1/deleteCustomerTeamMembers", {
        memberId,
      });
      toast.success("Team member has been removed from your organization.");
      setDeleteMemberId(null);
      await fetchTeamMembers();
    } catch (error) {
      console.error("Error removing customer team member:", error);
      toast.error((error as { message?: string })?.message || "Failed to remove team member.");
    } finally {
      setIsRemovingMember(false);
    }
  };


  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Team Members</h1>
          <p className="text-muted-foreground">Manage your team access</p>
        </div>
        <Dialog open={addMemberOpen} onOpenChange={handleAddMemberDialogChange}>
          <DialogTrigger asChild><Button><Users className="h-4 w-4 mr-2" />Add Member</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
              <DialogDescription>Invite a new member to join your team.</DialogDescription>
            </DialogHeader> 
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label htmlFor="member-first-name">First Name</Label><Input id="member-first-name" autoComplete="off" placeholder="Enter first name" value={newMemberForm.firstName} onChange={(e) => setNewMemberForm((prev) => ({ ...prev, firstName: e.target.value }))} /></div>
              <div className="space-y-2"><Label htmlFor="member-last-name">Last Name</Label><Input id="member-last-name" autoComplete="off" placeholder="Enter last name" value={newMemberForm.lastName} onChange={(e) => setNewMemberForm((prev) => ({ ...prev, lastName: e.target.value }))} /></div>
              <div className="space-y-2"><Label htmlFor="member-email">Email Address</Label><Input id="member-email" type="email" autoComplete="off" placeholder="name@company.com" value={newMemberForm.email} onChange={(e) => setNewMemberForm((prev) => ({ ...prev, email: e.target.value }))} /></div>
              <div className="space-y-2"><Label htmlFor="member-designation">Designation</Label><Input id="member-designation" autoComplete="off" placeholder="e.g. Finance Manager" value={newMemberForm.designation} onChange={(e) => setNewMemberForm((prev) => ({ ...prev, designation: e.target.value }))} /></div>
              <div className="space-y-2"><Label htmlFor="member-phone">Phone</Label><Input id="member-phone" autoComplete="off" inputMode="numeric" maxLength={15} placeholder="9876543210" value={newMemberForm.phone} onChange={(e) => setNewMemberForm((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, "").slice(0, 15) }))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleAddMemberDialogChange(false)} disabled={isInvitingMember}>Cancel</Button>
              <Button onClick={handleAddMember} disabled={!isAddMemberFormValid || isInvitingMember}>Send Invitation</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Designation</TableHead><TableHead>Phone</TableHead>
                <TableHead className="text-center">Status</TableHead><TableHead className="text-center">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No members are there
                  </TableCell>
                </TableRow>
              ) : (
                teamMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            {([member.first_name, member.last_name].filter(Boolean).map((n) => n[0]).join("") || member.name?.[0] || "?").toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">
                          {[member.first_name, member.last_name].filter(Boolean).join(" ") || member.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.designation}</TableCell>
                    <TableCell>{member.phone}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch checked={member.isActive} onCheckedChange={() => setToggleMemberId(member.id)} />
                        <span className={`text-xs ${member.isActive ? "text-green-600" : "text-muted-foreground"}`}>{member.isActive ? "Active" : "Inactive"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteMemberId(member.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteMemberId} onOpenChange={(open) => !open && !isRemovingMember && setDeleteMemberId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this team member? They will lose access to the portal immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemovingMember}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={isRemovingMember} onClick={() => deleteMemberId && handleDeleteMember(deleteMemberId)}>
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Toggle Status Confirmation Dialog */}
      <AlertDialog open={!!toggleMemberId} onOpenChange={(open) => !open && !isTogglingMemberStatus && setToggleMemberId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Change Member Status</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const member = teamMembers.find(m => m.id === toggleMemberId);
                return member?.isActive
                  ? "Are you sure you want to deactivate this member? They will lose access to the portal."
                  : "Are you sure you want to activate this member? They will gain access to the portal.";
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isTogglingMemberStatus}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={isTogglingMemberStatus} onClick={() => { if (toggleMemberId) { handleToggleMemberStatus(toggleMemberId); } }}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TeamMembersContent;
